import React, { createContext, useContext, useMemo } from 'react';
import { FOLLOWED_SCOPE, isFollowedScope, WhereaboutsScope } from '@features/whereabouts/models/whereaboutsScope';

// Where the header's back button goes on a scoped page. `teamsTab` is the MS
// Teams tab to deep-link to when running inside Teams.
export interface WhereaboutsBackLink {
  url: string;
  text: string;
  teamsTab?: string;
}

export interface WhereaboutsScopeContextValue {
  scope: WhereaboutsScope;
  backLink?: WhereaboutsBackLink;
  // Convenience flag: true when the page is pinned to one team/workgroup.
  isScoped: boolean;
}

const WhereaboutsScopeContext = createContext<WhereaboutsScopeContextValue>({
  scope: FOLLOWED_SCOPE,
  isScoped: false,
});

interface ProviderProps {
  scope?: WhereaboutsScope;
  backLink?: WhereaboutsBackLink;
  children: React.ReactNode;
}

export function WhereaboutsScopeProvider({ scope, backLink, children }: ProviderProps) {
  const resolvedScope = scope ?? FOLLOWED_SCOPE;
  const value = useMemo<WhereaboutsScopeContextValue>(() => ({
    scope: resolvedScope,
    backLink,
    isScoped: !isFollowedScope(resolvedScope),
  }), [resolvedScope, backLink]);

  return (
    <WhereaboutsScopeContext.Provider value={value}>
      {children}
    </WhereaboutsScopeContext.Provider>
  );
}

// The scope of the enclosing whereabouts page. Outside a provider this is the
// followed-groups scope, which keeps every existing caller behaving as before.
export function useWhereaboutsScope(): WhereaboutsScopeContextValue {
  return useContext(WhereaboutsScopeContext);
}
